export class StockfishEngine {
    constructor(options = {}) {
        this.engineUrl = options.engineUrl || '';
        this.depth = Number.isFinite(options.depth) ? options.depth : 21;
        this.multiPv = Number.isFinite(options.multiPv) ? options.multiPv : 3;
        this.onUpdate = typeof options.onUpdate === 'function' ? options.onUpdate : () => {};
        this.onReady = typeof options.onReady === 'function' ? options.onReady : () => {};
        this.onError = typeof options.onError === 'function' ? options.onError : console.error;

        this.worker = null;
        this.isStarted = false;
        this.isReady = false;
        this.isDestroyed = false;

        this.currentFen = null;
        this.analysisToken = 0;
        this.latestLines = new Map();

        this._boundHandleMessage = this._handleMessage.bind(this);
        this._boundHandleWorkerError = this._handleWorkerError.bind(this);
        this._readyResolver = null;
        this._readyPromise = null;

        // FIX: track a pending bestmove resolver so analyze() can wait for stop ack
        this._bestmoveResolver = null;
        this._isSearching = false;

        this.currentDepth = 0;
    }

    async start() {
        if (this.isDestroyed) {
            throw new Error('StockfishEngine: cannot start a destroyed engine');
        }

        if (this.isStarted) {
            return this._waitUntilReady();
        }

        if (!this.engineUrl) {
            throw new Error('StockfishEngine: missing engineUrl');
        }

        try {
            this.worker = new Worker(this.engineUrl);
            this.worker.addEventListener('message', this._boundHandleMessage);
            this.worker.addEventListener('error', this._boundHandleWorkerError);

            this.isStarted = true;
            this.isReady = false;
            this._readyPromise = new Promise((resolve) => {
                this._readyResolver = resolve;
            });

            this._post('uci');
            this._post('isready');

            return this._waitUntilReady();
        } catch (err) {
            this.onError('StockfishEngine failed to start', err);
            throw err;
        }
    }

    async analyze(fen, options = {}) {
        if (this.isDestroyed) {
            return;
        }

        const rawDepth = Number.isFinite(options.depth) ? options.depth : this.depth;
        const nextDepth = rawDepth >= 0 ? rawDepth : 0;
        const nextMultiPv = Number.isFinite(options.multiPv) ? options.multiPv : this.multiPv;

        await this.start();
        await this._waitUntilReady();

        if (this.isDestroyed) {
            return;
        }

        this.analysisToken += 1;
        const token = this.analysisToken;

        // Null out currentFen BEFORE stopping so that any stale `info` lines
        // emitted by the old search while we wait for bestmove are blocked by
        // the `if (!this.currentFen) return` guard in _handleInfoLine.
        // Setting currentFen = newFen here (before the stop) was the root cause
        // of the depth display bug: stale info lines at depth=33 were attributed
        // to the new FEN because the FEN guard passed, overwriting the reset
        // currentDepth=0 with the old search's depth.
        this.currentFen = null;
        this.latestLines.clear();
        this.currentDepth = 0;

        this.depth = nextDepth;
        this.multiPv = nextMultiPv;

        // FIX: if Stockfish is currently searching, send stop and wait for the
        // "bestmove" acknowledgement before issuing new position + go commands.
        // Sending "position" while the engine is still computing causes a WASM
        // "unreachable" crash because the engine's internal state is inconsistent.
        if (this._isSearching) {
            await this._stopAndWaitForBestmove();
        }

        // Guard: another analyze() call may have arrived while we were waiting
        if (this.isDestroyed || token !== this.analysisToken) {
            return;
        }

        // Safe to set the real FEN now — the old search has fully stopped and
        // no more stale info lines will arrive for the previous position.
        this.currentFen = fen || '';

        this._post(`setoption name MultiPV value ${this.multiPv}`);
        this._post(`position fen ${this.currentFen}`);
        this._isSearching = true;
        if (this.depth > 0) {
            this._post(`go depth ${this.depth}`);
        } else {
            this._post('go infinite');
        }

        return token;
    }

    stop() {
        if (!this.worker || this.isDestroyed) {
            return;
        }

        this.analysisToken += 1;
        this.currentFen = null;
        this.latestLines.clear();
        this.currentDepth = 0;

        if (this._isSearching) {
            this._post('stop');
            // Don't await here — stop() is fire-and-forget from callers.
            // _isSearching will be cleared when bestmove arrives.
        }
    }

    destroy() {
        if (this.isDestroyed) {
            return;
        }

        this.isDestroyed = true;
        this.isStarted = false;
        this.isReady = false;

        // Resolve any pending bestmove wait so callers don't hang
        if (this._bestmoveResolver) {
            this._bestmoveResolver();
            this._bestmoveResolver = null;
        }

        if (this.worker) {
            try {
                if (this._isSearching) {
                    this._post('stop');
                }
                this.worker.removeEventListener('message', this._boundHandleMessage);
                this.worker.removeEventListener('error', this._boundHandleWorkerError);
                this.worker.terminate();
            } catch (err) {
                this.onError('StockfishEngine failed to terminate worker', err);
            }
        }

        this.worker = null;
        this._readyResolver = null;
        this._readyPromise = null;
        this._bestmoveResolver = null;
        this._isSearching = false;
        this.latestLines.clear();
        this.currentDepth = 0;
    }

    // ─── private ────────────────────────────────────────────────────────────

    /**
     * Send "stop" and return a Promise that resolves when Stockfish confirms
     * with a "bestmove" line (or after a 500 ms safety timeout).
     */
    _stopAndWaitForBestmove() {
        return new Promise((resolve) => {
            if (!this.worker || !this._isSearching) {
                resolve();
                return;
            }

            // If there is already a pending resolver (shouldn't happen, but guard it)
            if (this._bestmoveResolver) {
                this._bestmoveResolver();
            }

            this._bestmoveResolver = resolve;

            // Safety timeout: if bestmove never arrives, unblock after 500 ms
            const timer = window.setTimeout(() => {
                if (this._bestmoveResolver === resolve) {
                    this._bestmoveResolver = null;
                    this._isSearching = false;
                    this.currentDepth = 0;
                    resolve();
                }
            }, 500);

            // Wrap resolve so we can also cancel the timer
            this._bestmoveResolver = () => {
                window.clearTimeout(timer);
                this._bestmoveResolver = null;
                this._isSearching = false;
                resolve();
            };

            this._post('stop');
        });
    }

    _waitUntilReady() {
        if (this.isReady) {
            return Promise.resolve();
        }
        return this._readyPromise || Promise.resolve();
    }

    _post(command) {
        if (!this.worker || this.isDestroyed) {
            return;
        }
        this.worker.postMessage(command);
    }

    _handleMessage(event) {
        const raw = typeof event.data === 'string' ? event.data.trim() : '';
        if (!raw) {
            return;
        }

        if (raw === 'readyok') {
            this.isReady = true;
            if (this._readyResolver) {
                this._readyResolver();
                this._readyResolver = null;
            }
            this.onReady();
            return;
        }

        // FIX: bestmove signals that the search has ended (either from explicit stop
        // or because it searched to the requested depth).
        if (raw.startsWith('bestmove')) {
            this._isSearching = false;
            if (this._bestmoveResolver) {
                this._bestmoveResolver();
                // resolver clears itself
            }
            return;
        }

        if (raw.startsWith('info ')) {
            this._handleInfoLine(raw);
            return;
        }
    }

    _handleWorkerError(err) {
        this.onError('StockfishEngine worker error', err);

        if (this.worker) {
            try {
                this.worker.removeEventListener('message', this._boundHandleMessage);
                this.worker.removeEventListener('error', this._boundHandleWorkerError);
                this.worker.terminate();
            } catch (e) {
                this.onError('StockfishEngine failed to terminate errored worker', e);
            }
        }

        this.analysisToken += 1;
        this.worker = null;
        this.isStarted = false;
        this.isReady = false;
        this.currentFen = null;
        this._isSearching = false;
        this.latestLines.clear();
        this.currentDepth = 0;

        if (this._bestmoveResolver) {
            this._bestmoveResolver();
        }
        this._bestmoveResolver = null;
        this._readyResolver = null;
        this._readyPromise = null;
    }

    _handleInfoLine(line) {
        if (!line.includes(' pv ')) {
            return;
        }
        if (!this.currentFen) {
            return;
        }

        const depthMatch = line.match(/\bdepth\s+(\d+)/);
        const multipvMatch = line.match(/\bmultipv\s+(\d+)/);
        const cpMatch = line.match(/\bscore\s+cp\s+(-?\d+)/);
        const mateMatch = line.match(/\bscore\s+mate\s+(-?\d+)/);
        const pvMatch = line.match(/\spv\s+(.+)$/);

        if (!multipvMatch || !pvMatch) {
            return;
        }

        const multipv = parseInt(multipvMatch[1], 10);
        const depth = depthMatch ? parseInt(depthMatch[1], 10) : null;
        const pv = pvMatch[1].trim();

        if (Number.isFinite(depth)) {
            this.currentDepth = depth;
        }

        let scoreType = null;
        let score = null;
        let scoreDisplay = '—';

        if (cpMatch) {
            scoreType = 'cp';
            score = parseInt(cpMatch[1], 10);
            scoreDisplay = this._formatCp(score);
        } else if (mateMatch) {
            scoreType = 'mate';
            score = parseInt(mateMatch[1], 10);
            scoreDisplay = this._formatMate(score);
        }

        const normalized = {
            multipv,
            depth,
            scoreType,
            score,
            scoreDisplay,
            pv,
            raw: line
        };

        this.latestLines.set(multipv, normalized);
        this._emitUpdate();
    }

    _emitUpdate() {
        const lines = Array.from(this.latestLines.values())
            .sort((a, b) => a.multipv - b.multipv)
            .slice(0, this.multiPv);

        this.onUpdate({
            fen: this.currentFen,
            depth: this.currentDepth,
            maxDepth: this.depth,
            multiPv: this.multiPv,
            lines
        });
    }

    _formatCp(cp) {
        const pawns = cp / 100;
        const abs = Math.abs(pawns).toFixed(2);
        return `${pawns < 0 ? '-' : ''}${abs}`;
    }

    _formatMate(mate) {
        if (mate === 0) {
            return 'M0';
        }
        return mate > 0 ? `#${mate}` : `-#${Math.abs(mate)}`;
    }
}